/**
 * 사이드바 메뉴 정의 — SM 포털 한 벌 (v2.135.0 · 2026-09-08 사용자 확정)
 *
 * 2026-09-06(v2.127.0)에 메뉴를 세 구역(전자결재 / 영업 / 재고)으로 갈랐다가 **되돌린 자리**다.
 * 되돌린 이유(사용자 말): 같은 화면으로 가는 문이 구역마다 생겨 「메뉴가 여기저기 중복으로 많고,
 * 정작 필요한 메뉴를 찾을 때 어렵다」. 권한이 없는 계정은 어차피 보이는 항목이 몇 개 안 된다.
 *
 * 그래서 지금은 —
 *   · 사이드바는 **그룹 4개짜리 한 벌**. 구역 탭도, 타일 홈도 없다.
 *   · 로그인하면 곧바로 **대시보드**(`/`). 대시보드에서 기술지원포털로 넘어가는 버튼을 둔다.
 *   · 기술지원포털은 별도 시스템이라 **통로만** 유지한다 — 대시보드 버튼 + 사이드바 맨 아래 링크.
 *   · 이름은 「SM 포털」 그대로 (영업만 쓰는 도구가 아니다).
 *
 * ⭐**주소(URL)는 한 개도 안 없앤다** — 구역 시절 주소(`/dashboard`·`/home`)로 들어와도 대시보드가
 *   열린다(App.tsx 가 `/` 로 넘긴다). 화면 안 탭 주소(`/expenses?tab=…`·`/stock?tab=…`)도 그대로다.
 *   즐겨찾기·웍스 알림 링크·안내 문서가 깨지지 않는다.
 *
 * ⭐메뉴는 **화면 하나에 한 줄**이다. 탭이 여럿인 화면(영업진행·사내결재 문서함·재고관리)은 화면 안에서 고른다
 *   — 구역 시절에는 탭마다 사이드바에 문을 냈고, 그게 「메뉴가 많아 보인다」의 절반이었다.
 *   다만 **브라우저 탭 제목**은 탭까지 구분해서 보여 준다(TAB_TITLES) — 창을 여럿 띄웠을 때 필요하다.
 */

import type { AuthUser } from './types';
import type { MenuIconName } from '../components/ui/MenuIcon';

export const APP_NAME = 'SM 포털';

export type MenuItem = {
  path: string;
  label: string;
  adminOnly?: boolean;
  adminMgmtOnly?: boolean;
  hideForViewer?: boolean;
  /** 👥 직원 명부 (v2.325.0) — 관리자 + 경영지원 지정만(로그인 정보 user.staffDirectory · 역할로는 못 가른다) */
  staffDirectory?: boolean;
  /** 결재 배지를 붙일 항목 — 내가 처리할 결재 건수(합계) */
  badge?: boolean;
  /**
   * 그림문자 대신 쓸 아이콘 그림(public 의 파일 주소). 있으면 label 앞에 그림문자를 붙이지 않는다
   * (v2.271.3 인포트렌드 잎 모양 · v2.271.4 나라장터 = 정부 상징 — 남의 로고는 그림문자로 나타낼 수 없다).
   */
  iconSrc?: string;
  /** 메뉴 아이콘 한 벌의 이름(components/ui/MenuIcon · v2.317.0) — 선 그림 + 종류 색. 있으면 사이드바는 그림문자 대신 이것을 그린다. label 의 그림문자는 남긴다(챗봇·개발요청·탭 제목이 label 을 쓴다). */
  icon?: MenuIconName;
};

export type MenuGroup = {
  heading: string;
  items: MenuItem[];
  /** 기본 접힘(v2.318.0 · 도구·관리) — 머리글을 누르면 펼치고 사람별로 기억한다(App.tsx). 켜진 메뉴가 안에 있으면 저절로 펼친다. */
  collapsible?: boolean;
};

/**
 * 사이드바 묶음 (2026-07-19 개편 → 2026-09-08 4묶음 복귀 → **v2.318.0 · 2026-10-31 8묶음**).
 *   경로·권한·화면은 그대로이고 **묶음 머리글과 순서만** 바뀐다(Salesforce 기능·메뉴 검토 보고서 I1 · 사용자 「추천안대로」).
 *   Salesforce 식 = 메뉴는 「업무 대상(오브젝트)」 이름으로 묶는다 · 기록끼리는 메뉴가 아니라 링크로 오간다 · 설정은 따로.
 *   · 홈        대시보드 · 일정
 *   · 영업      영업진행 · 딜등록 요청 · 유지보수 · 비상벨 — 우리 매출 4분류(일반 · 영업외 · 유지보수 · 비상벨)의 **장부**가 한 묶음
 *               (비상벨은 장비 판매 매출 대장이지 서비스가 아니다 · 딜등록은 영업건의 앞 단계)
 *   · 고객      거래처(← 관리 · 영업 계정에도 읽기로 열었다 · 이름도 「거래처 관리」→「거래처」) · 마케팅(← 분석·현황 · 명절선물·전시회·설문 = 고객에게 하는 일)
 *   · 재고·매입  재고관리 · 매입관리 · 수출입 — 물건의 흐름(수입 → 매입 → 재고 → 출고)
 *   · 문서·결재  내 결재함(진행 중 결재) → 거래 문서함 · 사내결재 문서함(완결 문서 보관) — 종전 그대로
 *   · 분석·자료  매출 분석 · 가격표 · 나라장터 · 사이징 툴 — 보고서와 참고 자료
 *   · 도구      메일 · NAS 파일탐색기 — 「본인 계정의 바깥 자료」라 문서함과 성격이 다르다 · 기본 접힘
 *   · 관리      변경 이력 · 시스템 관리 · 개발요청 — 운영 전용(Salesforce 의 Setup 자리) · 기본 접힘
 *   운영 접속 기록 5.5개월 실측(10-31): 직접 연 횟수 영업진행 655 ≫ 재고 337 > 매입 235 > 거래 문서함 133 > 결재함 114 …
 *   수출입 · 사이징 · NAS · 변경 이력은 37 이하 → 덜 쓰는 묶음(도구·관리)은 접어 둔다(결정 ⑥).
 */
export const MENU_GROUPS: MenuGroup[] = [
  {
    heading: '홈',
    items: [
      { path: '/', label: '📊 대시보드', icon: 'dashboard' },
      // v2.139.0: 웍스 회사 캘린더를 보고 직접 넣는 화면. 보기는 viewer 포함 전원, 등록·수정·삭제는 viewer 제외(화면도 서버도 각각 막는다).
      { path: '/calendar', label: '📅 일정', icon: 'calendar' },
      // v2.319.0: ✅ 할 일(Salesforce Tasks) — 영업건·거래처에 딸린 할 일과 그냥 할 일. viewer 는 영업건 담당이 아니라 없다(App.tsx ViewerBlocked 와 짝).
      { path: '/tasks', label: '✅ 할 일', icon: 'tasks', hideForViewer: true },
    ],
  },
  {
    heading: '영업',
    items: [
      // v2.249.0: 화면 안 탭 — 영업 목록 + 📄 판매보고서(viewer 에게는 탭 단추가 없다) · v2.290.0: 가운데 🧾 세금계산서 — 탭 셋
      { path: '/sales', label: '📋 영업진행', icon: 'sales' },
      // v2.140.0: 벤더 딜등록 목록(인포트렌드부터). 제안사 담당자 연락처·프로젝트 진행 내용이 들어 있어 viewer 에게는 감춘다 — 서버도 따로 막는다.
      { path: '/deal-registration', label: '🤝 딜등록 요청', icon: 'deal', hideForViewer: true },
      // v2.251.0: viewer 에게도 — 금액은 서버가 가리고(「***」) · 엑셀·예산 산정근거·계약 문서·쓰기는 viewer 에게 없다(서버도 sales 이상).
      { path: '/maintenance', label: '🔧 유지보수', icon: 'maintenance' },
      { path: '/bell', label: '🚨 비상벨', icon: 'bell', hideForViewer: true },
    ],
  },
  {
    heading: '고객',
    items: [
      // v2.143.0: [👤 거래처 담당자] 탭(주소록)이 생기며 영업 계정에도 열렸다 — 영업진행·딜등록에 적는 담당자를 누적 관리하는 자리라 영업이 주인이다.
      // v2.197.0: viewer 에게도 연다 — 읽기 전용(담당자 탭만). 쓰기는 서버에서도 sales 이상으로 막는다(화면 숨김 ≠ 서버 차단).
      // v2.318.0: 거래처 마스터 탭(사업자번호·주소·사업자등록증)을 **영업 계정에도 읽기로** 연다(F1 거래처 360 · 결정 ⑤ 「전부 읽기 · 쓰기는 admin·mgmt」).
      //   통장사본은 그대로 admin·mgmt 만. 이름은 「거래처 관리」→「거래처」(Salesforce 식 — 대상 이름으로).
      { path: '/partners', label: '🏢 거래처', icon: 'partners' },
      // v2.245.0: 📣 마케팅 = [🎁 명절선물] + [📋 부스 설문 집계] · v2.297.0 + 전시회. 받는 분 휴대폰·집 주소 · 지자체 담당자 연락처가 들어 있어
      //   viewer 에게는 감춘다 — 서버도 /holiday-gifts·/survey/booth/* 읽기를 sales 이상으로 막는다. 옛 주소 /survey-results 는 App.tsx 가 넘긴다.
      { path: '/marketing', label: '📣 마케팅', icon: 'marketing', hideForViewer: true },
    ],
  },
  {
    heading: '재고·매입',
    items: [
      { path: '/stock', label: '📦 재고관리', icon: 'stock' },
      { path: '/purchases', label: '🛒 매입관리', icon: 'purchase' },
      { path: '/imports', label: '🚢 수출입', icon: 'imports', adminMgmtOnly: true },
    ],
  },
  {
    // v2.50.0: 지출결재 3종은 전 직원(viewer 포함) 사용 → 메뉴를 전 계정에 노출. viewer 에게 가릴 것(판보·영업 문서 탭)은 각 화면 안에서 가린다.
    // v2.55.0 (2026-08-05 사용자 확정): 대외/대내 기준 — 거래 문서함 = 거래 상대와 주고받는 서류 / 사내결재 문서함 = 결재를 거치는 사내 문서.
    // v2.249.0 (2026-10-14 사용자 결정): 기준을 「이 문서를 영업번호로 찾나?」로 — 판매보고서(판보 보관함)는 영업진행 안의 탭으로,
    //   사내결재 문서함은 사람·돈·부서·기간의 문서만. 결재할 일은 종류 불문 내 결재함 한 곳.
    heading: '문서·결재',
    items: [
      { path: '/approvals', label: '🗂 내 결재함', icon: 'approvals', badge: true },
      { path: '/documents', label: '📁 거래 문서함', icon: 'documents' },
      { path: '/expenses', label: '🗄 사내결재 문서함', icon: 'expenses' },
    ],
  },
  {
    heading: '분석·자료',
    items: [
      { path: '/analysis', label: '📈 매출 분석', icon: 'analysis', hideForViewer: true },
      { path: '/price-list', label: '💰 주요항목 가격표', icon: 'price', hideForViewer: true },
      // v2.271.4 (2026-10-16 사용자 「나라장터 메뉴 아이콘도 나라장터 아이콘으로 · 조달청 로고」): 📑 → 정부 상징(태극)
      //   — 조달청·나라장터 누리집 아이콘이 둘 다 이 그림이다. 흰 바탕 원까지 그려야 어두운 메뉴 바탕에서 남색이 보인다.
      { path: '/g2b', label: '나라장터쇼핑몰 현황', iconSrc: '/gov-emblem.svg' },
      // v2.233.0: 인포트렌드 사이징 툴 — 엑셀(Sizing Tool v2.1)의 계산기를 화면으로. 서버 없이 화면 안에서만 계산하고,
      //   정가는 공개 MSRP(원가 아님)라 전 직원(viewer 포함)에게 연다. v2.271.3: 🧮 → 인포트렌드 잎 모양 그림
      { path: '/sizing-tool', label: '인포트렌드 사이징 툴', iconSrc: '/infortrend-mark.svg' },
    ],
  },
  {
    heading: '도구',
    collapsible: true,
    items: [
      // v2.205.0: 받은 메일 보기·쓰기·답장·전달 — 각자 연결한 **본인 웍스 메일함**을 그때그때 받아 보여 준다(DB 에 안 둔다).
      //   v2.250.0 (사용자 「메일 기능 뷰어 계정들도 쓰게」): 전 직원(viewer 포함). viewer 가 못 보는 금액 문서는 서버 첨부 단계(doc-source)가 막는다.
      { path: '/mail', label: '✉ 메일', icon: 'mail' },
      // v2.114.0: NAS 파일탐색기 — 전 직원. 각자 연결한 **본인 NAS 계정**이 열 수 있는 폴더다(권한 경계를 NAS 에 맡긴다).
      { path: '/nas-files', label: '🗃 NAS 파일탐색기', icon: 'nas' },
    ],
  },
  {
    heading: '관리',
    collapsible: true,
    items: [
      { path: '/audit-log', label: '🕘 변경 이력', icon: 'audit' },
      // v2.325.0: 👥 직원 명부 — 경영지원부가 직원 정보(이름·직급·부서·팀·휴대폰)를 고친다(사용자 「경영지원부에서 직원 목록·연락처 관리」).
      //   계정 권한(역할·비밀번호·2단계 인증·이메일·활성)은 시스템 관리 › 계정 관리(관리자) 그대로 · 서버 가드는 /admin/staff.
      { path: '/staff', label: '👥 직원 명부', icon: 'staff', staffDirectory: true },
      { path: '/system', label: '⚙️ 시스템 관리', icon: 'system', adminOnly: true },
      // v2.180.0: admin 전용 「🧭 개발 계획」 자리였다 → v2.274.0 (사용자 「작성된 건들은 지금의 개발계획 위치에 누적 게시 · 접근권한은 뷰어계정까지」):
      //   같은 자리·같은 주소가 **전 직원의 📮 개발요청 게시판**. 요청은 어느 화면에서든 오른쪽 위 단추로 쓴다. 서버 가드는 /dev-requests(역할 넷).
      { path: '/dev-plan', label: '📮 개발요청', icon: 'devreq' },
    ],
  },
];

/** 메뉴에 없는 공용 화면의 이름 — 브라우저 탭 제목용 */
const COMMON_TITLES: Record<string, string> = {
  '/mypage': '마이페이지',
  '/helpbot-link': '포털챗봇 연결',
  '/m/inventory': '재고 실사',
  '/m/stock-check': '주요재고 확인',
  '/m/stock-audit': '분기 재고 실사',
  '/m/stock-audit/:id': '분기 재고 실사',
  '/m/calendar': '일정',   // 폰 앱 일정 칸 (v2.232.0 · 점검 ③ H-2)
  '/login': '로그인',
  '/login/totp': '2차 인증',
  '/password-reset': '비밀번호 재설정',
  '/password-reset/confirm': '새 비밀번호 설정',
};

/**
 * 공용 화면 이름 — 같은 주소가 먼저, 없으면 `:id` 가 든 열쇠를 한 칸씩 맞춰 본다 (v2.232.0 · 점검 ③ H-1).
 *   전에는 그대로 찾기만 해서 `/m/stock-audit/12` 의 탭 제목이 「SM 포털」 뿐이었다(열쇠는 `/m/stock-audit/:id`).
 */
function commonTitle(pathname: string): string | undefined {
  const exact = COMMON_TITLES[pathname];
  if (exact) return exact;
  const segs = pathname.split('/');
  for (const [key, title] of Object.entries(COMMON_TITLES)) {
    if (!key.includes('/:')) continue;
    const ks = key.split('/');
    if (ks.length === segs.length && ks.every((k, i) => k.startsWith(':') ? segs[i] !== '' : k === segs[i])) return title;
  }
  return undefined;
}

/**
 * 탭이 여럿인 화면의 **브라우저 탭 제목** — 사이드바에는 한 줄이지만 제목은 탭까지 구분한다.
 * 창을 여럿 띄워 놓고 일하는 사람이 어느 창이 무엇인지 알아야 한다(v2.128.0 에서 얻은 것을 지킨다).
 * 키(탭 값)가 화면의 실제 탭과 어긋나면 `check:menu` 가 잡는다.
 */
const TAB_TITLES: Record<string, Record<string, string>> = {
  // v2.249.0: 영업진행 = 영업 목록 + 판매보고서(판보 보관함 — 사내결재 문서함에서 옮겨 왔다).
  //   목록 탭은 주소에 tab 이 없어 메뉴 이름(「영업진행」)으로 뜬다 — `list` 는 `?tab=list` 로 들어왔을 때를 위해 적어 둔다.
  //   v2.290.0: 세금계산서(발행요청 이력 · 수신·처리 내역 — 거래 문서함에서 옮겨 왔다).
  '/sales': {
    list: '영업진행',
    taxInvoice: '세금계산서',
    report: '판매보고서 보관함',
  },
  '/expenses': {
    stats: '지출 현황',
    docs: '지출결재 문서',
    card: '법인카드 사용내역',
    holiday: '휴일근무일지',
    // v2.280.0: 기술지원팀·영업팀 두 탭이 [부서별 월간보고서] 하나로 — 옛 값(tsp·salesReport)은 Expenses.tsx 가 이 탭으로 넘긴다
    monthly: '부서별 월간보고서',
    meeting: '회의록',
    stockAudit: '분기 재고 실사',
  },
  '/stock': {
    stock: '재고 현황',
    release: '출고 관리',
    manufacture: '제조',
    item: '품명·품목',
    movement: '입출고 내역',
    check: '주간 확인 이력',
    audit: '실사·실물확인',
  },
  // v2.143.0: 거래처 = 거래처 마스터 + 거래처 담당자 주소록 (v2.318.0: 메뉴 이름 「거래처 관리」→「거래처」)
  '/partners': {
    companies: '거래처',
    contacts: '거래처 담당자',
  },
  // v2.245.0: 마케팅 = 명절선물 + 부스 설문 집계 · v2.297.0: + 행사(개발요청 #13) · v2.300.0: 「행사」 → 「전시회」(탭 키 events 는 주소 호환으로 그대로)
  '/marketing': {
    gifts: '명절선물',
    events: '전시회',
    survey: '부스 설문 집계',
  },
};

/** 검사 그물이 읽는다 — 탭 제목 표의 화면·탭 키 목록 */
export function tabTitleEntries(): { path: string; tab: string }[] {
  return Object.entries(TAB_TITLES).flatMap(([path, tabs]) =>
    Object.keys(tabs).map((tab) => ({ path, tab })),
  );
}

/* ── 권한 필터 ─────────────────────── */
export function visibleItems(items: MenuItem[], user: AuthUser | null): MenuItem[] {
  const role = user?.role;
  return items
    .filter((r) => !r.adminOnly || role === 'admin')
    .filter((r) => !r.adminMgmtOnly || role === 'admin' || role === 'mgmt')
    .filter((r) => !r.staffDirectory || role === 'admin' || (role !== 'viewer' && user?.staffDirectory === true))
    .filter((r) => !r.hideForViewer || role !== 'viewer');
}

/* ── 활성 항목 판정 ─────────────────────── */
export function pathOnly(p: string): string {
  return p.split('?')[0] ?? p;
}

/**
 * 사이드바에서 지금 켜질 항목. 경로만 본다 — `/expenses?tab=card` 도 「사내결재 문서함」이 켜진다
 * (탭은 화면 안에서 고르는 것이지 메뉴 항목이 아니다).
 */
export function isItemActive(item: MenuItem, pathname: string): boolean {
  return pathOnly(item.path) === pathname;
}

/* ── 브라우저 탭 제목 ─────────────────────── */
/** 메뉴 이름 앞의 그림문자(이모지)를 뗀다 — 「🗂 내 결재함」→「내 결재함」 */
export function stripEmoji(label: string): string {
  return label.replace(/^[^\p{L}\p{N}(]+\s*/u, '').trim();
}

/**
 * 현재 주소의 화면 이름 — 「내 결재함 · SM 포털」. 브라우저 탭·방문 기록·즐겨찾기에 쓰인다.
 * 탭이 여럿인 화면은 탭 이름을 쓰고(`?tab=card` → 「법인카드 사용내역」), 그 밖에는 메뉴 이름을 쓴다.
 */
export function pageTitle(pathname: string, search: string): string {
  const tabs = TAB_TITLES[pathname];
  if (tabs) {
    const cur = new URLSearchParams(search).get('tab');
    const named = cur ? tabs[cur] : undefined;
    if (named) return `${named} · ${APP_NAME}`;
  }
  for (const g of MENU_GROUPS) {
    for (const it of g.items) {
      // 대시보드(`/`)는 옛 주소(`/dashboard`·`/home`)로 들어와도 같은 화면이다
      if (pathOnly(it.path) === pathname) return `${stripEmoji(it.label)} · ${APP_NAME}`;
    }
  }
  const common = commonTitle(pathname);
  return common ? `${common} · ${APP_NAME}` : APP_NAME;
}
